import { createContext, useState, useEffect, ReactNode, useMemo } from 'react';
import type { Session, SupabaseClient, AuthChangeEvent } from '@supabase/supabase-js';
import { AuthService } from '../authService';
import { AuthUIConfig, AuthUIContext } from './ui';
import { formatAuthConfigIssues, validateAuthUIConfig } from './configValidation';

type DebugWindow = Window & { __DG_DEBUG?: boolean };

const isDebugEnabled = (): boolean =>
  typeof window !== 'undefined' && (window as DebugWindow).__DG_DEBUG === true;

const getNodeEnv = (): string | undefined => {
  const maybeProcess = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process;
  return maybeProcess?.env?.NODE_ENV;
};

export interface SupabaseAuthContextValue {
  session: Session | null;
  isLoading: boolean;
  supabaseClient: SupabaseClient;
  authService: AuthService;
}

export const SupabaseAuthContext = createContext<SupabaseAuthContextValue | undefined>(undefined);

interface SupabaseAuthProviderProps {
  children: ReactNode;
  supabaseClient: SupabaseClient;
  ui?: AuthUIConfig;
}

export const SupabaseAuthProvider = ({ children, supabaseClient, ui }: SupabaseAuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Stable service instance bound to this supabaseClient
  const authService = useMemo(() => new AuthService(supabaseClient), [supabaseClient]);

  useEffect(() => {
    let mounted = true;

    // Initial session resolves loading (do not set loading true again later)
    supabaseClient.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setIsLoading(false);
      if (isDebugEnabled()) {
        console.log('[auth:getSession:init]', { hasSession: !!session });
      }
    });

    // Subscribe to auth changes (do not toggle isLoading true on refocus)
    const { data: { subscription } } = authService.onAuthStateChange(
      (event: AuthChangeEvent, next: Session | null) => {
        if (!mounted) return;
        setSession(next);

        if (isDebugEnabled()) {
          console.log('[auth:event]', event, {
            hasSession: !!next,
            expires_at: next?.expires_at,
            sub: next?.user?.id?.slice?.(0, 8),
          });
        }

        // Only clear loading on the initial event; never set it back to true later
        if (event === 'INITIAL_SESSION') {
          setIsLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
    // Important: do NOT include isLoading in deps — we only initialize once per client
  }, [supabaseClient, authService]);

  useEffect(() => {
    if (!ui) return;

    const isDevEnv = getNodeEnv() !== 'production';

    if (!isDevEnv) return;

    const issues = validateAuthUIConfig(ui);
    if (issues.length > 0) {
      const formatted = formatAuthConfigIssues(issues).join('\n');
      console.warn(`[supabase-client] AuthUIConfig issues:\\n${formatted}`);
    }
  }, [ui]);

  const value: SupabaseAuthContextValue = useMemo(
    () => ({
      session,
      isLoading,
      supabaseClient,
      authService,
    }),
    [session, isLoading, supabaseClient, authService]
  );

  return (
    <SupabaseAuthContext.Provider value={value}>
      <AuthUIContext.Provider value={ui}>{children}</AuthUIContext.Provider>
    </SupabaseAuthContext.Provider>
  );
};
